// Firebase Authentication Module for ThinqBits (Custom UI)
import {
    getAuth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    onAuthStateChanged,
    signOut,
    updateProfile
} from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import Analytics, { app } from './firebase-config.js';

// Use the shared Firebase app instance (initialized in firebase-config.js)
const auth = getAuth(app);
const db = getFirestore(app);

// Toggle between login and register forms
const showRegisterBtn = document.getElementById('show-register');
const showLoginBtn = document.getElementById('show-login');
const loginForm = document.getElementById('login-form');
const registerForm = document.getElementById('register-form');

if (showRegisterBtn) {
    showRegisterBtn.addEventListener('click', () => {
        loginForm.classList.add('hidden');
        registerForm.classList.remove('hidden');
    });
}

if (showLoginBtn) {
    showLoginBtn.addEventListener('click', () => {
        registerForm.classList.add('hidden');
        loginForm.classList.remove('hidden');
    });
}

// Handle Login
const loginFormElement = document.getElementById('login-form-element');
if (loginFormElement) {
    loginFormElement.addEventListener('submit', async (e) => {
        e.preventDefault();

        const email = document.getElementById('login-email').value;
        const password = document.getElementById('login-password').value;
        const errorDiv = document.getElementById('login-error');
        const submitBtn = document.getElementById('login-btn');

        // Clear previous errors
        errorDiv.classList.add('hidden');
        errorDiv.textContent = '';

        // Show loading
        submitBtn.disabled = true;
        submitBtn.textContent = 'Signing in...';

        try {
            await signInWithEmailAndPassword(auth, email, password);
            // Success - auth state listener will handle redirect
            Analytics.trackSignIn('email');
        } catch (error) {
            console.error('Login error:', error);

            let errorMessage = 'Failed to sign in. Please try again.';
            if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
                errorMessage = 'Incorrect email or password. Please try again or create a new account.';
            } else if (error.code === 'auth/wrong-password') {
                errorMessage = 'Incorrect password.';
            } else if (error.code === 'auth/invalid-email') {
                errorMessage = 'Invalid email address.';
            } else if (error.code === 'auth/too-many-requests') {
                errorMessage = 'Too many failed attempts. Please try again later.';
            }

            errorDiv.textContent = errorMessage;
            errorDiv.classList.remove('hidden');

            // Restore button
            submitBtn.disabled = false;
            submitBtn.textContent = 'Sign In';
        }
    });
}

// Handle Registration
const registerFormElement = document.getElementById('register-form-element');
if (registerFormElement) {
    registerFormElement.addEventListener('submit', async (e) => {
        e.preventDefault();

        const name = document.getElementById('register-name').value;
        const email = document.getElementById('register-email').value;
        const password = document.getElementById('register-password').value;
        const organization = document.getElementById('register-organization').value;
        const role = document.getElementById('register-role').value;
        const errorDiv = document.getElementById('register-error');
        const submitBtn = document.getElementById('register-btn');

        // Clear previous errors
        errorDiv.classList.add('hidden');
        errorDiv.textContent = '';

        // Show loading
        submitBtn.disabled = true;
        submitBtn.textContent = 'Creating account...';

        try {
            // Create user account
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            console.log('User created:', user.uid, user.email);

            // Track sign up
            Analytics.trackSignUp('email');

            // Update profile with display name
            await updateProfile(user, {
                displayName: name
            });

            console.log('Profile updated with name:', name);

            // Ensure the user is authenticated before writing to Firestore
            // Get fresh ID token to ensure auth is fully propagated
            console.log('Getting fresh ID token...');
            await user.getIdToken(true);
            console.log('ID token obtained');

            // Save to waitlist in Firestore
            try {
                console.log('Attempting to save to Firestore...');
                console.log('User UID:', user.uid);
                console.log('Collection: waitlist');
                console.log('Document ID:', user.uid);

                const docRef = doc(db, 'waitlist', user.uid);
                console.log('Document reference created:', docRef.path);

                await setDoc(docRef, {
                    uid: user.uid,
                    email: user.email,
                    fullName: name,
                    organization: organization || '',
                    role: role || '',
                    status: 'waitlist',
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });

                console.log('✅ SUCCESS: Account created and profile saved to Firestore!');
                console.log('Document path:', docRef.path);

                // Verify the write by reading it back
                const verifyDoc = await getDoc(docRef);
                if (verifyDoc.exists()) {
                    console.log('✅ VERIFIED: Document exists in Firestore:', verifyDoc.data());
                } else {
                    console.error('❌ ERROR: Document was not found after write!');
                }
            } catch (firestoreError) {
                console.error('❌ Firestore save error:', firestoreError);
                console.error('Error code:', firestoreError.code);
                console.error('Error message:', firestoreError.message);
                console.error('Full error:', firestoreError);

                // Show the error to the user
                errorDiv.textContent = 'Account created but profile save failed: ' + firestoreError.message;
                errorDiv.classList.remove('hidden');

                submitBtn.disabled = false;
                submitBtn.textContent = 'Create Account';

                throw new Error('Failed to save profile: ' + firestoreError.message);
            }

            // Success - auth state listener will handle redirect
        } catch (error) {
            console.error('Registration error:', error);
            console.error('Error code:', error.code);
            console.error('Error message:', error.message);

            let errorMessage = 'Failed to create account. Please try again.';
            if (error.code === 'auth/email-already-in-use') {
                errorMessage = 'An account with this email already exists.';
            } else if (error.code === 'auth/invalid-email') {
                errorMessage = 'Invalid email address.';
            } else if (error.code === 'auth/weak-password') {
                errorMessage = 'Password should be at least 6 characters.';
            }

            errorDiv.textContent = errorMessage;
            errorDiv.classList.remove('hidden');

            // Restore button
            submitBtn.disabled = false;
            submitBtn.textContent = 'Create Account';
        }
    });
}

// Track if we've already processed auth state to prevent loops
let authStateProcessed = false;
let lastProcessedPath = '';

// Auth state observer
onAuthStateChanged(auth, async (user) => {
    const currentPath = window.location.pathname;
    console.log('═══════════════════════════════════════════════');
    console.log('[auth.js] Auth state changed');
    console.log('[auth.js] User:', user ? user.email : 'No user');
    console.log('[auth.js] Path:', currentPath);
    console.log('[auth.js] Already processed:', authStateProcessed);
    console.log('[auth.js] Last processed path:', lastProcessedPath);
    console.log('═══════════════════════════════════════════════');

    if (user) {
        // Check if user has completed profile and their waitlist status
        try {
            const userDoc = await getDoc(doc(db, 'waitlist', user.uid));

            if (userDoc.exists()) {
                const userData = userDoc.data();
                console.log('User profile exists:', userData);

                // Check waitlist status
                const status = userData.status || 'waitlist';
                console.log('User status:', status);

                if (currentPath === '/login' || currentPath === '/login.html') {
                    if (status === 'approved') {
                        // User is approved, check if onboarding is complete
                        if (authStateProcessed && lastProcessedPath === currentPath) {
                            console.log('Already processed redirect for approved user on login, skipping');
                        } else {
                            const onboardingCompleted = userData.onboardingCompleted || false;
                            console.log('User approved, onboarding completed:', onboardingCompleted);

                            authStateProcessed = true;
                            lastProcessedPath = currentPath;

                            if (!onboardingCompleted) {
                                console.log('Redirecting to onboarding');
                                window.location.href = '/onboarding';
                            } else {
                                console.log('Redirecting to app');
                                window.location.href = '/app';
                            }
                        }
                    } else {
                        // User is on waitlist, show waitlist screen (only once per path)
                        if (authStateProcessed && lastProcessedPath === currentPath) {
                            console.log('Already showed waitlist screen, skipping');
                        } else {
                            console.log('User on waitlist, showing waitlist screen');
                            authStateProcessed = true;
                            lastProcessedPath = currentPath;
                            showWaitlistScreen(user.email);
                        }
                    }
                } else if (currentPath === '/app' || currentPath === '/app.html') {
                    // User trying to access app
                    if (status !== 'approved') {
                        // Not approved, redirect to login/waitlist (only once)
                        if (authStateProcessed && lastProcessedPath === currentPath) {
                            console.log('Already processed redirect for non-approved user, skipping');
                        } else {
                            console.log('User not approved, redirecting to login');
                            authStateProcessed = true;
                            lastProcessedPath = currentPath;
                            window.location.href = '/login';
                        }
                    } else {
                        // User is approved, check if onboarding is complete
                        if (authStateProcessed && lastProcessedPath === currentPath) {
                            console.log('Already processed approved user on app, skipping');
                        } else {
                            const onboardingCompleted = userData.onboardingCompleted || false;
                            console.log('User approved, onboarding completed:', onboardingCompleted);

                            authStateProcessed = true;
                            lastProcessedPath = currentPath;

                            // If onboarding not complete, redirect to onboarding
                            if (!onboardingCompleted) {
                                console.log('Onboarding not complete, redirecting to onboarding');
                                window.location.href = '/onboarding';
                            } else {
                                console.log('User approved, allowing access to app');
                            }
                        }
                    }
                } else if (currentPath === '/onboarding' || currentPath === '/onboarding.html') {
                    // User on onboarding page
                    if (status !== 'approved') {
                        // Not approved, redirect to login/waitlist
                        if (authStateProcessed && lastProcessedPath === currentPath) {
                            console.log('Already processed redirect for non-approved user on onboarding, skipping');
                        } else {
                            console.log('User not approved, redirecting to login from onboarding');
                            authStateProcessed = true;
                            lastProcessedPath = currentPath;
                            window.location.href = '/login';
                        }
                    } else {
                        // User is approved, allow access to onboarding
                        console.log('User on onboarding page, allowing access');
                        authStateProcessed = true;
                        lastProcessedPath = currentPath;
                    }
                }
            } else {
                // User authenticated but no waitlist profile found
                // This means registration didn't complete properly
                console.error('User authenticated but no waitlist profile found!');
                console.error('User UID:', user.uid);
                console.error('This user needs to be added to waitlist manually or re-register');

                // Stay on login page and show error instead of redirecting
                if (currentPath === '/login' || currentPath === '/login.html') {
                    if (authStateProcessed && lastProcessedPath === currentPath) {
                        console.log('Already showed waitlist screen for missing profile, skipping');
                    } else {
                        console.log('Showing waitlist screen with missing profile warning');
                        authStateProcessed = true;
                        lastProcessedPath = currentPath;
                        showWaitlistScreen(user.email);
                    }
                } else if (currentPath === '/app' || currentPath === '/app.html') {
                    // Redirect to login since they don't have proper access
                    if (authStateProcessed && lastProcessedPath === currentPath) {
                        console.log('Already redirected for missing profile, skipping');
                    } else {
                        console.log('No profile found, redirecting to login');
                        authStateProcessed = true;
                        lastProcessedPath = currentPath;
                        window.location.href = '/login';
                    }
                }
            }
        } catch (error) {
            console.error('Error checking user profile:', error);
        }
    } else {
        // User is signed out
        console.log('User signed out');
        authStateProcessed = false; // Reset flag on signout
        lastProcessedPath = '';

        // If we're on app page and not authenticated, redirect to login
        if (currentPath === '/app' || currentPath === '/app.html') {
            window.location.href = '/login';
        }
    }
});

// Show waitlist screen
let waitlistLogoutSetup = false;

function showWaitlistScreen(email) {
    const loginForm = document.getElementById('login-form');
    const registerForm = document.getElementById('register-form');
    const waitlistScreen = document.getElementById('waitlist-screen');
    const waitlistEmail = document.getElementById('waitlist-email');

    if (loginForm) loginForm.classList.add('hidden');
    if (registerForm) registerForm.classList.add('hidden');
    if (waitlistScreen) waitlistScreen.classList.remove('hidden');
    if (waitlistEmail) waitlistEmail.textContent = email;

    // Set up logout button only once
    if (!waitlistLogoutSetup) {
        const logoutBtn = document.getElementById('waitlist-logout-btn');
        if (logoutBtn) {
            logoutBtn.addEventListener('click', async () => {
                try {
                    await signOut(auth);
                    // Reset flag on signout
                    waitlistLogoutSetup = false;
                    // Will trigger auth state change and redirect
                } catch (error) {
                    console.error('Error signing out:', error);
                }
            });
            waitlistLogoutSetup = true;
        }
    }
}

// Export auth utilities for use in other modules
export { auth, db, signOut, onAuthStateChanged };

// Global auth helper functions
window.thinqbitsAuth = {
    getCurrentUser: () => auth.currentUser,

    getIdToken: async (forceRefresh = false) => {
        const user = auth.currentUser;
        if (user) {
            try {
                // Force refresh if token is older than 55 minutes (tokens expire at 60 min)
                return await user.getIdToken(forceRefresh);
            } catch (error) {
                console.error('Error getting ID token:', error);
                // If token is expired, force refresh and retry once
                if (error.code === 'auth/user-token-expired') {
                    console.log('Token expired, forcing refresh...');
                    return await user.getIdToken(true);
                }
                throw error;
            }
        }
        return null;
    },

    signOut: async () => {
        try {
            await signOut(auth);
            window.location.href = '/';
        } catch (error) {
            console.error('Error signing out:', error);
            showError('Failed to sign out. Please try again.');
        }
    },

    checkWaitlistStatus: async () => {
        const user = auth.currentUser;
        if (!user) return null;

        try {
            const userDoc = await getDoc(doc(db, 'waitlist', user.uid));
            if (userDoc.exists()) {
                return userDoc.data();
            }
            return null;
        } catch (error) {
            console.error('Error checking waitlist status:', error);
            return null;
        }
    }
};

// Helper function to show errors
function showError(message) {
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
    toast.innerHTML = `
        <div class="flex items-center">
            <i class="fas fa-exclamation-circle mr-2"></i>
            <span>${message}</span>
        </div>
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
        toast.classList.add('animate-fade-out');
        setTimeout(() => toast.remove(), 300);
    }, 4000);
}

// Helper function to show success messages
function showSuccess(message) {
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
    toast.innerHTML = `
        <div class="flex items-center">
            <i class="fas fa-check-circle mr-2"></i>
            <span>${message}</span>
        </div>
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
        toast.classList.add('animate-fade-out');
        setTimeout(() => toast.remove(), 300);
    }, 3000);
}
